import {isClientRecoveryEnabled, observeClientRecovery} from '@github-ui/ahp-relay/relay-client-recovery'
import type {EstablishedAhpSessionIdentity} from '@github-ui/ahp-session-client/contracts'
import {createContext, useCallback, useEffect, useRef} from 'react'

import {
  type CopilotClientRecoveryOwner,
  type CopilotRecoveryControls,
  createCopilotClientRecoveryOwner,
} from './copilot-client-recovery'
import type {UiReadinessSurface} from './ui-readiness-contract'
import {useOneCopilotBrowserTelemetryEnabled} from './use-one-copilot-browser-telemetry-enabled'

export type ClientRecoveryControlsObserver = (controls: CopilotRecoveryControls) => (() => void) | undefined
export const CopilotClientRecoveryContext = createContext<ClientRecoveryControlsObserver | undefined>(undefined)

export function useCopilotClientRecovery(
  identity: EstablishedAhpSessionIdentity | undefined,
  surface?: UiReadinessSurface,
): ClientRecoveryControlsObserver | undefined {
  const enabled = useOneCopilotBrowserTelemetryEnabled(isClientRecoveryEnabled())
  const ownerRef = useRef<{view: CopilotClientRecoveryOwner; key: string} | undefined>(undefined)
  const mountedRef = useRef(false)
  const {taskId, sessionId, environmentId, channel} = identity ?? {}
  const select = useCallback(
    () =>
      observeClientRecovery(() => {
        if (!enabled || !taskId || !sessionId || !environmentId) {
          ownerRef.current?.view.dispose()
          ownerRef.current = undefined
          return
        }
        const selected = {taskId, sessionId, environmentId, channel}
        const key = JSON.stringify([taskId, sessionId, environmentId, channel ?? null, surface ?? null])
        if (ownerRef.current?.key !== key) {
          ownerRef.current?.view.dispose()
          ownerRef.current = undefined
        }
        ownerRef.current ??= {view: createCopilotClientRecoveryOwner(surface), key}
        if (!ownerRef.current.view.select(selected)) return
        return ownerRef.current.view
      }),
    [enabled, taskId, sessionId, environmentId, channel, surface],
  )
  const controls = useCallback(
    (value: CopilotRecoveryControls) => observeClientRecovery(() => select()?.controls(value)),
    [select],
  )
  useEffect(() => {
    select()
  }, [select])
  useEffect(() => {
    if (!enabled) return
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      queueMicrotask(() => {
        if (mountedRef.current) return
        ownerRef.current?.view.dispose()
        ownerRef.current = undefined
      })
    }
  }, [enabled])
  return enabled ? controls : undefined
}
